import JSZip from 'jszip';
import { ExportPackConfig, ExportTableRequest, ExportScenarioViewRequest } from 'object-models/ExportPackModel';
import { exportCharts, ChartExportError, describeChartExportError } from './chartExportPipeline';

/** Replace any character that is not alphanumeric, dot, hyphen, or underscore */
const sanitizeFilename = (name: string): string =>
  name.replace(/[^A-Za-z0-9._-]/g, '_').replace(/_+/g, '_');

export interface ChartExportOptions {
  container: HTMLDivElement;
  currency: string;
  locationId: number;
  ghgScope: string[];
  startYear?: number | null;
  endYear?: number | null;
}

export interface ExportPackOutcome {
  chartErrors: ChartExportError[];
  chartCount: number;
}

export const generateExportPack = async (
  config: ExportPackConfig,
  request: ExportTableRequest,
  apiCalls: {
    exportProjectRegister: (req: ExportTableRequest) => Promise<Blob>;
    exportScenarioView: (req: ExportScenarioViewRequest) => Promise<Blob>;
    exportInputDataPack: (locationId: number) => Promise<Blob>;
  },
  locationName: string,
  allScenarios: string[] = [],
  chartOptions?: ChartExportOptions
): Promise<ExportPackOutcome> => {
  const zip = new JSZip();
  const dateStr = new Date().toISOString().split('T')[0];
  const chartErrors: ChartExportError[] = [];
  let chartCount = 0;

  const scenarios = config.scenarioMode === 'all'
    ? allScenarios
    : config.selectedScenarios;

  // Project Register: one file per selected scenario
  if (config.includeProjectRegister) {
    for (const scenario of scenarios) {
      const tableReq: ExportTableRequest = { ...request, format: config.tableFormat, scenario };
      const blob = await apiCalls.exportProjectRegister(tableReq);
      const safeName = sanitizeFilename(scenario);
      zip.file(`Tables/ProjectRegister_${safeName}_${dateStr}.${config.tableFormat}`, blob);
    }
  }

  // Decarbonization Plans: single file with all selected scenarios as columns
  if (config.includeScenarioView) {
    const scenarioViewReq: ExportScenarioViewRequest = {
      locationId: request.locationId,
      scenarios,
      format: config.tableFormat,
      locationName: request.locationName,
    };
    const blob = await apiCalls.exportScenarioView(scenarioViewReq);
    zip.file(`Tables/DecarbonizationPlans_${dateStr}.${config.tableFormat}`, blob);
  }

  if (config.includeInputDataPack) {
    const blob = await apiCalls.exportInputDataPack(request.locationId);
    zip.file(`Tables/InputDataPack_${dateStr}.xlsx`, blob);
  }

  // Charts: off-screen render and capture
  const someChartsSelected = Object.values(config.charts).some(Boolean);
  if (someChartsSelected && chartOptions) {
    const { results, errors } = await exportCharts({
      config,
      scenarios,
      locationId: chartOptions.locationId,
      ghgScope: chartOptions.ghgScope,
      startYear: chartOptions.startYear,
      endYear: chartOptions.endYear,
      currency: chartOptions.currency,
      container: chartOptions.container,
    });
    for (const result of results) {
      zip.file(result.filename, result.blob);
    }
    chartCount = results.length;
    chartErrors.push(...errors);

    if (errors.length > 0) {
      const lines = [
        'Some charts could not be generated for this Export Pack.',
        '',
        ...errors.map(describeChartExportError),
      ];
      zip.file('Charts/_errors.txt', lines.join('\n'));
    }
  }

  const content = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(content);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeFilename(locationName)}_ExportPack_${dateStr}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return { chartErrors, chartCount };
};
